import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  FundDraftService,
  IFundDraft,
  IFundSeriesResponse,
  IPerformanceRecord,
} from '../app/services/FundDraftService'

interface IFundSeriesState {
  data: IFundSeriesResponse | null
  draft: IFundDraft | null
  performanceRecords: Record<string, IPerformanceRecord[]>
  fundId: string | null
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
}

const initialState: IFundSeriesState = {
  data: null,
  draft: null,
  performanceRecords: {},
  fundId: null,
  status: 'idle',
}

export const fetchSeriesOnly = createAsyncThunk(
  'fundSeries/fetchSeriesOnly',
  async (fundId: string) => {
    return FundDraftService().getSeries(fundId)
  }
)

export const fetchFundData = createAsyncThunk(
  'fundSeries/fetchAll',
  async (fundId: string) => {
    const svc = FundDraftService()

    const [series, draft] = await Promise.all([
      svc.getSeries(fundId),
      svc.getDraft(fundId).catch(() => null),
    ])

    const performanceEntries = await Promise.all(
      series.series.map(async (s) => {
        const records = await svc
          .getPerformanceRecords(fundId, s.serie_codigo)
          .catch(() => [] as IPerformanceRecord[])
        return [s.serie_codigo, records] as [string, IPerformanceRecord[]]
      })
    )

    return {
      series,
      draft,
      performanceRecords: Object.fromEntries(performanceEntries),
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as { fundSeries: IFundSeriesState }
      return state.fundSeries.status !== 'loading'
    },
  }
)

export const fetchDraftOnly = createAsyncThunk(
  'fundSeries/fetchDraftOnly',
  async (fundId: string) => {
    return FundDraftService().getDraft(fundId)
  }
)

export const fetchPerformanceForSerie = createAsyncThunk(
  'fundSeries/fetchPerformanceForSerie',
  async ({ fundId, serieCode }: { fundId: string; serieCode: string }) => {
    const records = await FundDraftService().getPerformanceRecords(fundId, serieCode)
    return { serieCode, records }
  }
)

export const fundSeriesSlice = createSlice({
  name: 'fundSeries',
  initialState,
  reducers: {
    clearFundSeries: (state) => {
      state.data = null
      state.draft = null
      state.performanceRecords = {}
      state.fundId = null
      state.status = 'idle'
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFundData.pending, (state, action) => {
        state.status = 'loading'
        state.fundId = action.meta.arg
      })
      .addCase(fetchFundData.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.data = action.payload.series
        state.draft = action.payload.draft
        state.performanceRecords = action.payload.performanceRecords
      })
      .addCase(fetchFundData.rejected, (state) => {
        state.status = 'failed'
      })
      .addCase(fetchSeriesOnly.fulfilled, (state, action) => {
        state.data = action.payload
      })
      .addCase(fetchDraftOnly.fulfilled, (state, action) => {
        state.draft = action.payload
      })
      .addCase(fetchPerformanceForSerie.fulfilled, (state, action) => {
        state.performanceRecords = {
          ...state.performanceRecords,
          [action.payload.serieCode]: action.payload.records,
        }
      })
  },
})

export const { clearFundSeries } = fundSeriesSlice.actions
export default fundSeriesSlice.reducer
